import { Directive, ElementRef, Input, OnInit } from '@angular/core';
import { AUTO_STYLE, keyframes, style } from '@angular/animations';

import { NgStaticAnimationService } from '../../shared/services/static-animation.service';
import { EnumNgEventName } from '../../shared/event-animation.data';
import { INgAnimationStates, INgAnimationTiming, INgEffectOptions } from '../../shared/event-animation.interface';


/**------------------------------------------------------
 * Animation State Definitions
 */
function skew(skewValue: number | undefined): INgAnimationStates {

	//0 - set the default if no specific value is given
	skewValue = skewValue ? skewValue : 10;

	//1 - create the animations state
	const animationStates: INgAnimationStates = {
		start 	: keyframes([
			style({
				visibility				: AUTO_STYLE,
				transform				: `skew(-${skewValue}deg)`,
				'transition-property'	: 'transform',
				easing					: 'ease',
				offset					: 0
			}),
			style({
				transform				: 'skew(0)',
				'transition-property'	: 'transform',
				easing					: 'ease',
				offset					: 1
			})
		]),
		end		: keyframes([
			style({
				visibility				: AUTO_STYLE,
				transform				: 'skew(0)',
				'transition-property'	: 'transform',
				easing					: 'ease',
				offset					: 0
			}),
			style({
				transform				: `skew(-${skewValue}deg)`,
				'transition-property'	: 'transform',
				easing					: 'ease',
				offset					: 1
			})
		])
	};
	return animationStates;
}


/**------------------------------------------------------
 * Skew on Event
 */
@Directive({
    selector: '[ngSkewOnEvent]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgSkewOnEventDirective implements OnInit {

	//** Input Decorators */
	@Input() effectOptions: Partial<INgCustomEffectOptions> = null!;
	@Input() targetElement: HTMLElement = null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the skew value is relative to the animation functionality
		if (this.effectOptions?.skew && this.effectOptions?.skew < 1) throw new Error(`SkewOnEventDirective => ngOnInit => FATAL ERROR: Provided skew value should be positive number: ${this.effectOptions?.skew}`);

		//1 - setup the animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: skew(this.effectOptions?.skew),
			event			: this.effectOptions?.event!,
			timing			: {
				duration	: this.effectOptions?.duration,
				delay		: this.effectOptions?.delay
			}
		});
	}
}


/**------------------------------------------------------
 * Skew on Mouse Hover
 */
@Directive({
    selector: '[ngSkewOnMouseHover]',
    providers: [NgStaticAnimationService],
    standalone: false
})
export class NgSkewOnMouseHoverDirective implements OnInit {

	//** Input Decorators */
	@Input() timingOptions: Partial<INgCustomAnimationTiming> = null!;
	@Input() targetElement: HTMLElement 					= null!;

	constructor(
		private elementRef				: ElementRef,
		private staticAnimationService 	: NgStaticAnimationService
	) {}

	//** Setup the Animation */
	ngOnInit(): void {

		//0 - check the skew value is relative to the animation functionality
		if (this.timingOptions?.skew && this.timingOptions?.skew < 1) throw new Error(`SkewOnMouseHoverDirective => ngOnInit => FATAL ERROR: Provided skew value should be positive number: ${this.timingOptions?.skew}`);

		//1 - setup the animation
		this.staticAnimationService.setupAnimation({
			triggerElement	: this.elementRef.nativeElement,
			targetElement	: this.targetElement,
			states			: skew(this.timingOptions?.skew),
			event			: EnumNgEventName.MouseHover,
			timing			: this.timingOptions
		});
	}
}


//** Interfaces --------------------------------- */
interface INgCustomEffectOptions extends INgEffectOptions {
	skew : number;
}

interface INgCustomAnimationTiming extends INgAnimationTiming {
	skew : number;
}
